import React, { useState, useEffect } from 'react';
import Button from '@hubins/components/Button';
import SearchInput from 'components/SearchInput';
import { formatMoney } from 'helpers';
import { LoadingBar } from '@hubins/components/LoadingIndicators';
import __ from 'localisation';
import ApiPaginationTable from 'modules/Tables/ApiPaginationTable';
import { Columns, Column } from 'components/Columns';
import BackofficeProfileLink from '@hubins/components/BackofficeProfileLink';
import BackofficePortfolioLink from '@hubins/components/BackofficePortfolioLink/BackofficePortfolioLink';
import './index.scss';

import { useMiddleman } from 'helpers/MiddlemanContext';

const loadingObj = {
  'toolsBeginning': <LoadingBar small />,
  'user_name': <LoadingBar small />,
  'shortName': <LoadingBar small />,
  'portfolio': {
    available_balance: <LoadingBar small />,
    market_value: <LoadingBar small />,
  }
};

const loadingState = [
  loadingObj,
  loadingObj,
  loadingObj,
];

const headers = [
  {
    Header: '',
    accessor: 'toolsBeginning',
    className: 'rt-cell-button',
    width: 40
  },
  {
    Header: __('name'),
    accessor: 'name',
    Cell: ({ row }) => (
      <>
        {row?.original?.name && (
          <BackofficeProfileLink profileUuid={row?.original?.uuid}>
            {row?.original?.name}
          </BackofficeProfileLink>
        )}
      </>
    )
  },
  {
    Header: __('ssn_org_num'),
    accessor: "national_identity_number",
    Cell: ({ row }) => (
      <>
        {row?.original?.national_identity_number && (
          <BackofficeProfileLink profileUuid={row?.original?.uuid}>
            {row?.original?.national_identity_number}
          </BackofficeProfileLink>
        )}
      </>
    )
  },
  {
    Header: __('distributor'),
    accessor: "portfolio.distributor"
  },
  {
    Header: __('policy'),
    accessor: 'portfolio.short_name',
    Cell: ({ row }) => (
      <>
        {row?.original?.portfolio?.short_name && (
          <BackofficePortfolioLink
            portfolioUuid={row?.original?.portfolio?.uuid}
            profileUuid={row?.original?.uuid}
          >
            {row?.original?.portfolio?.short_name}
          </BackofficePortfolioLink>
        )}
      </>
    )
  },
  {
    Header: __('account_balance'),
    accessor: 'portfolio.available_balance',
    Cell: ({ row }) => formatMoney(row.original.portfolio.available_balance, row.original.currency)
  },
  {
    Header: __('market_value'),
    accessor: 'portfolio.market_value',
    Cell: ({ row }) => formatMoney(row.original.portfolio.market_value, row.original.currency)
  },
  {
    Header: __('updated_at'),
    accessor: 'portfolio.updated_at'
  },
];

const AllClientsAndPortfolios = () => {
  const middleman = useMiddleman();

  const [clients, setClients] = useState(false);
  const [amountOfPages, setAmountOfPages] = useState(-1);
  const [search, setSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState(0);
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPage = (page, searchParam = false) => {
    setSelectedPage(page - 1);
    setLoading(true);
    middleman.get(`/ql/crm/search?page=${page}${searchParam ? `&search=${searchParam}` : ''}`)
      .then(res => {
        const formattedRes = res.data.data.reduce((users, item) => {
          if (item.ins_portfolios && Array.isArray(item.ins_portfolios)) {
            item.ins_portfolios.forEach((policy) => {
              users.push({
                ...item,
                portfolio: policy,
                toolsBeginning: <Button
                  // If user has multiple portfolios, link to portfolio page, otherwise redirect to client page
                  href={item.ins_portfolios.length > 0 ? `/dashboard/clients/${item.uuid}/${policy.uuid}/tabs` : `/dashboard/profiles/${item.uuid}`}
                  className={`transparent`}
                  icon="arrow-right"
                  onlyIcon
                />
              });
            });
          }
          return users;

        }, []);
        setAmountOfPages(res.data.last_page);
        setClients(formattedRes);
        setLoading(false);
      });
  };

  const searchCallback = () => {
    fetchPage(1, search);
  };

  useEffect(() => {
    if (init) {
      fetchPage(1, search);
      const timeout = setTimeout(searchCallback, 1500);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [search]);

  const handlePageFetch = (index) => {
    fetchPage(index + 1, search);
  };

  const handleSearchChange = (e) => {
    if (!init) {
      setInit(true);
    }

    setSearch(e.target.value);
  };

  return (
    <Columns>
      <Column s="12" className="flex flex--row align-items-center space-between">
        <SearchInput onChange={handleSearchChange} value={search} placeholder="Search client name, portfolio, policy or SSN/Org-nr" loading={loading} className="all-clients__header-searchinput" />
        <Button href="/dashboard/create" icon="add" size="large">
          {__("create")}
        </Button>
      </Column>
      {init && (
        <Column s="12">
          <ApiPaginationTable
            data={clients ? clients : loadingState}
            headers={headers}
            fetchData={handlePageFetch}
            pageCount={amountOfPages}
            index={selectedPage}
            preventInitalFetch
          />
        </Column>
      )}
    </Columns>
  );
};

export default AllClientsAndPortfolios;
